import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaEdit } from "react-icons/fa";
import { Modal, Button, Form } from "react-bootstrap";
import "../../styles/Form.css";
import "../../styles/AdminUserTable.css";
import useAccess from "../../hooks/useAccess";
import DataTable from "../../components/DataTable";
import StyledSelect from "../../components/StyledSelect";
import PermissionNotice from "../../components/PermissionNotice";
import ReportFilterPanel from "../../components/ReportFilterPanel";
import AdminPageHeader from "../../components/AdminPageHeader";
import "../../styles/AdministrationPages.css";

function AdminUserPage() {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  const [adminUsers, setAdminUsers] = useState([]);
  const [roleMap, setRoleMap] = useState({});
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newStatus, setNewStatus] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [statusOptions, setStatusOptions] = useState([]);

  const { hasPermission, permissionsLoaded } = useAccess();

  const PERMISSIONS = {
    users: {
      create: hasPermission("AdminUser", "User", "edit"),
      edit: hasPermission("AdminUser", "User", "edit"),
      delete: hasPermission("AdminUser", "User", "delete"),
    },
  };

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setAdminUsers(res.data.filter((user) => user.role === 1));
      })
      .catch((err) => console.error("Failed to fetch admin users:", err));

    axios
      .get(`${BASE_URL}/api/dropdowns/user-status`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setStatusOptions(res.data))
      .catch((err) =>
        console.error("Failed to fetch user status options:", err)
      );
  }, [BASE_URL, token]);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/dropdowns/emp-role`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const map = {};
        res.data.forEach((role) => {
          map[role.r_id] = role.r_name;
        });
        setRoleMap(map);
      })
      .catch((err) => console.error("Failed to fetch emp roles:", err));
  }, [BASE_URL, token]);

  const renderEmpRoles = (rolesJson) => {
    if (!rolesJson) return "";
    try {
      const ids = Array.isArray(rolesJson)
        ? rolesJson
        : JSON.parse(rolesJson || "[]");
      if (!Array.isArray(ids)) return "";
      return ids.map((id) => roleMap[id]).filter(Boolean).join(", ");
    } catch {
      return "";
    }
  };

  const getStatusIdByName = useCallback((name) => {
    const match = statusOptions.find((status) => status.s_TranslatedText === name);
    return match ? match.s_id.toString() : null;
  }, [statusOptions]);

  const filteredUsers = useMemo(
    () =>
      adminUsers.filter((user) => {
        const matchesSearch = Object.values(user).some((value) =>
          value?.toString().toLowerCase().includes(searchQuery.toLowerCase())
        );
        const matchesStatus =
          statusFilter === "All" ||
          user.status?.toString() === getStatusIdByName(statusFilter);
        return matchesSearch && matchesStatus;
      }),
    [adminUsers, searchQuery, statusFilter, getStatusIdByName]
  );

  const handleEdit = (id) =>
    navigate(`/edit-user/${id}?source=admin-users`);

  const handleDelete = (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    axios
      .delete(`${BASE_URL}/api/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        alert("User deleted successfully");
        setAdminUsers((prev) => prev.filter((user) => user.id !== id));
      })
      .catch(() => alert("Failed to delete user"));
  };

  const openStatusModal = (user) => {
    if (!PERMISSIONS.users.edit) return;
    setSelectedUser(user);
    setNewStatus(user.status);
    setShowStatusModal(true);
  };

  const saveStatus = async () => {
    try {
      await axios.put(
        `${BASE_URL}/api/admin-users/${selectedUser.id}/status`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setAdminUsers((prev) =>
        prev.map((user) =>
          user.id === selectedUser.id ? { ...user, status: newStatus } : user
        )
      );

      setShowStatusModal(false);
      alert("Status updated successfully");
    } catch (err) {
      alert("Failed to update status");
    }
  };

  const columns = [
    {
      key: "full_name",
      label: "Full Name",
      cellClassName: "fullname-cell",
      render: (row) => row.full_name,
    },
    {
      key: "userid",
      label: "Username",
      render: (row) => row.userid || "-",
    },
    {
      key: "emp_roles",
      label: "Role",
      getSortValue: (row) => renderEmpRoles(row.emp_roles),
      cellClassName: "role-cell",
      render: (row) => <div className="role-text">{renderEmpRoles(row.emp_roles)}</div>,
    },
    {
      key: "email",
      label: "Email",
      cellClassName: "email-cell",
      render: (row) => <div className="email-text">{row.email}</div>,
    },
    {
      key: "status",
      label: "Status",
      width: "150px",
      headerClassName: "admin-status-column text-center",
      cellClassName: "admin-status-column",
      getSortValue: (row) => (row.status === 1 ? "Active" : "Inactive"),
      render: (row) => (
        <span
          className={`status-badge ${row.status === 1 ? "active" : "inactive"}`}
          style={{
            cursor: PERMISSIONS.users.edit ? "pointer" : "not-allowed",
          }}
          onClick={() => openStatusModal(row)}
          title={
            PERMISSIONS.users.edit
              ? "Click to change status"
              : "No permission to edit user status"
          }
        >
          {row.status === 1 ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      sortable: false,
      width: "160px",
      headerClassName: "admin-action-column text-center",
      cellClassName: "actions-cell admin-action-column",
      render: (row) => (
        <div className="actions-wrapper">
          <button
            className="app-table-action-button is-edit"
            onClick={() => handleEdit(row.id)}
            disabled={!PERMISSIONS.users.edit}
            title={
              PERMISSIONS.users.edit
                ? "Edit user"
                : "No permission to edit users"
            }
            aria-label="Edit user"
          >
            <FaEdit />
          </button>
          {PERMISSIONS.users.delete && (
            <button
              className="app-table-action-button is-delete"
              onClick={() => handleDelete(row.id)}
            >
              <i className="fas fa-trash" aria-hidden="true" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="admin-standard-page">
      <AdminPageHeader
        icon="fas fa-user-shield"
        title="Admin Users"
        description="Manage administrator accounts, roles, access status, and account details."
        actions={PERMISSIONS.users.create && (
          <button
            className="btn btn-primary dashboard-add-button"
            onClick={() => navigate("/form")}
          >
            Add New User <i className="fas fa-plus" aria-hidden="true" />
          </button>
        )}
      />

      <section className="admin-standard-workspace">
        <div className="admin-standard-workspace-body">

      {permissionsLoaded &&
        (!PERMISSIONS.users.create ||
          !PERMISSIONS.users.edit ||
          !PERMISSIONS.users.delete) && (
          <PermissionNotice
            message={`Limited access: you cannot ${[
              !PERMISSIONS.users.create && "create",
              !PERMISSIONS.users.edit && "edit",
              !PERMISSIONS.users.delete && "delete",
            ]
              .filter(Boolean)
              .join(", ")} user records.`}
            compact
          />
        )}

      <ReportFilterPanel>
        <div className="admin-standard-filter-grid">
          <label className="admin-standard-filter-field">
            <span>Search</span>
          <input
            type="search"
            className="form-control"
            placeholder="Name, username, role, or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          </label>
          <label className="admin-standard-filter-field">
            <span>Status</span>
          <StyledSelect
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: "All", label: "All" },
              ...statusOptions.map((status) => ({
                value: status.s_TranslatedText,
                label: status.s_TranslatedText,
              })),
            ]}
            minWidth={140}
            ariaLabel="Filter admin users by status"
          />
          </label>
        </div>
      </ReportFilterPanel>

      <DataTable
        columns={columns}
        data={filteredUsers}
        rowKey="id"
        itemsPerPage={20}
        tableClassName="admin-user-table"
        wrapperClassName="mt-3"
        emptyMessage="No admin users found."
      />
        </div>
      </section>

      <Modal
        show={showStatusModal}
        onHide={() => setShowStatusModal(false)}
        centered
        size="sm"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Update Status</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Select
            value={newStatus}
            onChange={(e) => setNewStatus(Number(e.target.value))}
          >
            <option value={1}>Active</option>
            <option value={5}>Inactive</option>
          </Form.Select>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveStatus}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default AdminUserPage;
